/**
 * AddNoteDialog — shared component for logging interactions (call / WhatsApp / note).
 *
 * Two usage modes:
 *
 * 1. API mode (CRMLeadTable): pass `leadId` + `onSaved`.
 *    The component calls crmService.addNote() itself and fires `onSaved` on success.
 *
 * 2. Local mode (CRMLeadForm): pass `onSave(noteData)` without `leadId`.
 *    The component calls `onSave` with the constructed note object; the parent
 *    appends it to local state and saves it as part of the full lead form submit.
 *
 * Props:
 *   open          — controls dialog visibility
 *   category      — pre-selected category: "call" | "whatsapp" | "internal" | "email" | "meeting" | "other"
 *   leadId        — numeric lead ID (API mode only)
 *   leadLabel     — display string shown in the dialog subtitle (e.g. "Acme Corp · John · 9876543210")
 *   authorName    — name to stamp on the note
 *   contacts      — optional list of contacts to pick "Contact Person" from (used in CRMLeadForm)
 *   onClose       — called when the dialog should close (cancel or after save)
 *   onSaved       — called after a successful API save (API mode)
 *   onSave        — called with the note payload (local mode); return value is ignored
 */

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  Calendar,
  Clock,
  Loader2,
  Smile,
  Meh,
  Frown,
  Tag,
  User,
  AlertCircle,
} from "lucide-react";
import { crmService } from "../crmApi";

// ─── Types ────────────────────────────────────────────────────────────────────

export type NoteCategory =
  | "call"
  | "whatsapp"
  | "email"
  | "meeting"
  | "internal"
  | "other";

export interface NotePayload {
  author: string;
  text: string;
  category: NoteCategory;
  sentiment: "positive" | "neutral" | "negative";
  priority: "low" | "medium" | "high" | "urgent" | "";
  status: "completed" | "scheduled" | "postponed";
  next_meeting: string | null;
  next_meeting_time: string | null;
  contact_name: string | null;
}

export interface ContactOption {
  id: string | number;
  name: string;
  type?: string;
}

interface AddNoteDialogProps {
  open: boolean;
  category?: NoteCategory;
  /** API mode: numeric lead ID — component will POST to /notes/ */
  leadId?: number | null;
  /** Human-readable subtitle shown in the dialog */
  leadLabel?: string;
  authorName: string;
  /** Optional list of contacts for the "Contact Person" picker */
  contacts?: ContactOption[];
  onClose: () => void;
  /** API mode: fired after successful POST */
  onSaved?: () => void;
  /** Local mode: fired with the note payload so the parent can append to state */
  onSave?: (note: NotePayload) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<NoteCategory, string> = {
  call: "📞 Log Call",
  whatsapp: "💬 Log WhatsApp",
  email: "✉️ Log Email",
  meeting: "🤝 Log Meeting",
  internal: "📝 Add Note",
  other: "📋 Add Note",
};

const CATEGORY_HEADING_COLORS: Record<NoteCategory, string> = {
  call: "text-blue-600",
  whatsapp: "text-green-600",
  email: "text-purple-600",
  meeting: "text-orange-600",
  internal: "text-gray-700",
  other: "text-gray-700",
};

const CATEGORY_OPTIONS: { value: NoteCategory; label: string }[] = [
  { value: "call", label: "Phone Call" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "meeting", label: "In-person Meeting" },
  { value: "internal", label: "Internal Note" },
  { value: "other", label: "Other" },
];

const TIME_OPTIONS = [
  { value: "any", label: "Any time" },
  { value: "morning", label: "Morning (8 AM – 12 PM)" },
  { value: "afternoon", label: "Afternoon (12 PM – 4 PM)" },
  { value: "evening", label: "Evening (4 PM – 8 PM)" },
  { value: "night", label: "Night (8 PM – 12 AM)" },
  ...Array.from({ length: 24 }, (_, i) => {
    const hour = i === 0 ? 12 : i > 12 ? i - 12 : i;
    const ampm = i < 12 ? "AM" : "PM";
    const label = `${hour}:00 ${ampm}`;
    return { value: label, label };
  }),
];

// ─── Component ────────────────────────────────────────────────────────────────

const AddNoteDialog: React.FC<AddNoteDialogProps> = ({
  open,
  category: initialCategory = "internal",
  leadId,
  leadLabel,
  authorName,
  contacts = [],
  onClose,
  onSaved,
  onSave,
}) => {
  const { toast } = useToast();

  const [category, setCategory] = React.useState<NoteCategory>(initialCategory);
  const [text, setText] = React.useState("");
  const [sentiment, setSentiment] = React.useState<"positive" | "neutral" | "negative">("neutral");
  const [noteStatus, setNoteStatus] = React.useState<"completed" | "scheduled" | "postponed">("completed");
  const [priority, setPriority] = React.useState<"low" | "medium" | "high" | "urgent" | "">("");
  const [nextMeeting, setNextMeeting] = React.useState("");
  const [nextMeetingTime, setNextMeetingTime] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // Sync category when prop changes (e.g. user clicks Call vs WhatsApp)
  React.useEffect(() => {
    setCategory(initialCategory);
  }, [initialCategory]);

  // Reset all fields when dialog opens
  React.useEffect(() => {
    if (open) {
      setCategory(initialCategory);
      setText("");
      setSentiment("neutral");
      setNoteStatus("completed");
      setPriority("");
      setNextMeeting("");
      setNextMeetingTime("");
      setContactName(contacts.length > 0 ? String(contacts[0].name) : "");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildPayload = (): NotePayload => ({
    author: authorName,
    text: text.trim(),
    category,
    sentiment,
    priority,
    status: noteStatus,
    next_meeting: nextMeeting || null,
    next_meeting_time: nextMeetingTime === "any" ? null : nextMeetingTime || null,
    contact_name: contactName || null,
  });

  const handleSave = async () => {
    if (!text.trim()) {
      toast({ title: "Note text is required", variant: "destructive" });
      return;
    }

    const payload = buildPayload();

    // ── Local mode (CRMLeadForm — no leadId yet) ──────────────────────────
    if (!leadId && onSave) {
      onSave(payload);
      toast({
        title: "Note added",
        description: "Interaction log will be saved with the lead.",
      });
      onClose();
      return;
    }

    // ── API mode (CRMLeadTable — leadId known) ────────────────────────────
    if (leadId) {
      setSaving(true);
      try {
        await crmService.addNote({ lead: leadId, ...payload });
        toast({
          title: "Saved",
          description: "Interaction logged successfully.",
        });
        onSaved?.();
        onClose();
      } catch (err: any) {
        toast({
          title: "Failed to save note",
          description: err?.response?.data?.detail || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    // Fallback: neither leadId nor onSave provided — shouldn't happen
    toast({ title: "Configuration error", variant: "destructive" });
  };

  const isApiMode = !!leadId;
  const heading = CATEGORY_LABELS[category];
  const headingColor = CATEGORY_HEADING_COLORS[category];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      {/*
        Mobile: bottom sheet (fixed, full-width, rounded top)
        Desktop (sm+): standard centered dialog, max-w-md
      */}
      <DialogContent
        className={[
          "w-full p-0 gap-0 overflow-hidden",
          // Mobile bottom sheet
          "fixed bottom-0 left-0 right-0 top-auto translate-x-0 translate-y-0",
          "rounded-t-2xl rounded-b-none max-w-none",
          // Desktop override
          "sm:relative sm:bottom-auto sm:left-[50%] sm:top-[50%]",
          "sm:translate-x-[-50%] sm:translate-y-[-50%]",
          "sm:fixed sm:rounded-xl sm:max-w-md",
        ].join(" ")}
      >
        {/* Drag handle — mobile only */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>

        <DialogHeader className="px-5 pt-3 pb-2 sm:pt-5">
          <DialogTitle className={`text-base font-semibold ${headingColor}`}>
            {heading}
          </DialogTitle>
          {leadLabel && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{leadLabel}</p>
          )}
        </DialogHeader>

        {/* Scrollable body */}
        <div className="px-5 pb-2 flex flex-col gap-3 max-h-[70vh] overflow-y-auto">

          {/* Row 1: Contact Person + Category */}
          <div className="grid grid-cols-2 gap-3">
            {contacts.length > 0 && (
              <div className="flex flex-col gap-1">
                <Label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                  <User className="h-3 w-3" /> Contact
                </Label>
                <Select value={contactName} onValueChange={setContactName}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.name}>
                        {c.name}{c.type ? ` (${c.type})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className={`flex flex-col gap-1 ${contacts.length === 0 ? "col-span-2" : ""}`}>
              <Label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                <Tag className="h-3 w-3" /> Category
              </Label>
              <Select value={category} onValueChange={(v) => setCategory(v as NoteCategory)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Note text */}
          <div className="flex flex-col gap-1">
            <Label htmlFor="qn-text" className="text-xs font-medium text-gray-700">
              Note <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="qn-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                category === "call"
                  ? "What was discussed on the call?"
                  : category === "whatsapp"
                  ? "Summary of WhatsApp conversation…"
                  : category === "meeting"
                  ? "Meeting notes…"
                  : "Note…"
              }
              className="text-sm resize-none min-h-[80px]"
              autoFocus
            />
          </div>

          {/* Sentiment picker */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-medium text-gray-700 flex items-center gap-1">
              <Smile className="h-3 w-3" /> Customer Sentiment
            </Label>
            <div className="flex gap-2">
              {(["positive", "neutral", "negative"] as const).map((s) => {
                const Icon = s === "positive" ? Smile : s === "neutral" ? Meh : Frown;
                const active =
                  s === "positive"
                    ? "bg-green-50 border-green-500 text-green-600 ring-1 ring-green-500"
                    : s === "neutral"
                    ? "bg-amber-50 border-amber-500 text-amber-600 ring-1 ring-amber-500"
                    : "bg-red-50 border-red-500 text-red-600 ring-1 ring-red-500";
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSentiment(s)}
                    className={`flex-1 py-2 rounded-md border flex items-center justify-center transition-all ${
                      sentiment === s ? active : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-xs font-medium text-gray-700">Status</Label>
              <Select value={noteStatus} onValueChange={(v) => setNoteStatus(v as typeof noteStatus)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="postponed">Postponed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Priority
              </Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as typeof priority)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator className="my-1" />

          {/* Follow-up details */}
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Follow-up Details (Optional)
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="qn-next" className="text-xs font-medium text-gray-700 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Next Follow-up
              </Label>
              <div className="relative">
                <Input
                  id="qn-next"
                  type="date"
                  value={nextMeeting}
                  onChange={(e) => setNextMeeting(e.target.value)}
                  className="h-9 text-sm pl-7"
                />
                <Calendar className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Time
              </Label>
              <Select value={nextMeetingTime} onValueChange={setNextMeetingTime}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Any time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {TIME_OPTIONS.slice(0, 5).map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    {TIME_OPTIONS.slice(5).map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 pb-5 pt-3 flex flex-row gap-2 sm:justify-end border-t border-gray-100 mt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={saving}
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !text.trim()}
            className="flex-1 sm:flex-none bg-intellend-green hover:bg-intellend-green/90 text-white"
          >
            {saving && isApiMode ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                Saving…
              </>
            ) : (
              "Save Note"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddNoteDialog;
